import { useEffect, useState, useCallback } from "react";
import { Button, Card, Row, Col } from "antd";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { useGeolocated } from "react-geolocated";
import { PageHeaderControl, } from "@seims/shared";
import { useOfflineAttendeeCount } from "../../../hooks/useOfflineMutationQueue";

import eventsService from "../../../services/eventsService";

import '../eventDetails.css'

const tabStyle = (active) => ({
    borderRadius: 20,
    padding: "6px 18px",
    fontWeight: 500,
    border: "1px solid #1F7AB6",
    background: active ? "#1F7AB6" : "#fff",
    color: active ? "#fff" : "#1F7AB6",
});

export const CaptureAttendee = () => {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const location = useLocation();
    const isOnline = navigator.onLine;

    const { coords, isGeolocationEnabled, isGeolocationAvailable, getPosition } =
      useGeolocated({
        positionOptions: {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: Infinity,
        },
        isOptimisticGeolocationEnabled: false,
        // suppressLocationOnMount: true,
        watchPosition: true,
        userDecisionTimeout: 10000,
      });

    // Event name from Listing
    const { eventName } =  location.state || "Event - Capture Attendees"
    const [onlineCount, setOnlineCount] = useState(0);
    const attendeeCount = useOfflineAttendeeCount();

    const loadAttendeeCounts = useCallback(async () => {
        try {
            const res = await eventsService.getEventParticipants(eventId);
            if (res?.status === 200) {
                setOnlineCount(res.data?.totalParticipants ?? 0);
            }
        } catch (e) {
            console.error("Failed to load attendee count", e);
        }
    }, [eventId]);

    useEffect(() => {
        loadAttendeeCounts();
    }, [eventId, loadAttendeeCounts]);

    useEffect(() => {
        const handleSyncComplete = () => {
            console.log("[Attendees] Sync complete → refreshing counts");
            loadAttendeeCounts();
        };

        window.addEventListener("offline-queue-sync-complete", handleSyncComplete);

        return () => {
            window.removeEventListener("offline-queue-sync-complete", handleSyncComplete);
        };
    }, [loadAttendeeCounts]);

    const isActive = (tab) =>
        location.pathname.endsWith(tab) ||
        (tab === "individual" && location.pathname.endsWith("/attendees"));
    
    const contextValue = {
        coords,
        isGeolocationEnabled,
        isGeolocationAvailable,
        getPosition,
        loadAttendeeCounts,
        setOnlineCount,
        locationReady: isGeolocationEnabled && coords
    };

    return (
        <div>
            <PageHeaderControl title={eventName}>
            <Row justify="center">
                <Col xs={24} sm={24} md={22} lg={18} xl={16}>
                    {/*  Page Header */}
                    <Card style={{
                        borderRadius: 0,
                        marginBottom: 0,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                marginBottom: 12,
                            }}
                        >
                            {/* <h2 style={{ margin: 0 }}>Capture Attendees</h2> */}

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "auto auto",
                                        columnGap: 8,
                                        rowGap: 2,
                                        alignItems: "center",
                                    }}
                                >
                                    {/* Online */}
                                    <span style={{ color: "#008000", fontWeight: 600 }}>
                                        Total Captured:
                                    </span>
                                    <span style={{ color: "#008000", fontWeight: 600 }}>
                                        {onlineCount}
                                    </span>

                                    {/* Offline , hide total offline captured when online*/}
                                    {!isOnline && (
                                        <>
                                            <span style={{ color: "#999", fontWeight: 600 }}>
                                                Total Offline Captured:
                                            </span>
                                            <span style={{ color: "#999", fontWeight: 600 }}>
                                                {attendeeCount || 0}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                        {/* Tabs Row */}
                        <div className="capture-tabs-container">
                            <Button
                                data-testid="capture-tab-individual"
                                style={tabStyle(isActive("individual"))}
                                onClick={() => navigate(`/events/capture/${eventId}/attendees/individual`, { state: { eventName } })}
                            >
                                Individual
                            </Button>

                            <Button
                                data-testid="capture-tab-group"
                                style={tabStyle(isActive("group"))}
                                onClick={() => navigate(`/events/capture/${eventId}/attendees/group`, { state: { eventName } })}
                            >
                                Group
                            </Button>

                            <Button
                                data-testid="capture-tab-science-facilitator"
                                style={tabStyle(isActive("science-facilitator"))}
                                onClick={() => navigate(`/events/capture/${eventId}/attendees/science-facilitator`, { state: { eventName } })}
                            >
                                Science Facilitator
                            </Button>

                            {/* <div className={`gps-text-container ${isGeolocationEnabled ? 'gps-online' : 'gps-offline'}`}>
                                <span className={`gps-status-dot ${coords ? 'gps-dot-active' : 'gps-dot-inactive'}`}/>
                                <span> {gpsStatus} </span>
                            </div> */}
                        </div>
                    </Card>

                    {/* Tab Content */}
                    <Outlet context={contextValue} />
                </Col>
            </Row>
            </PageHeaderControl>
        </div>
    );
};
